import { useEffect, useRef, useState } from "react";
import { addAlias, fetchBand, fetchBands, removeAlias, saveBand, type BandDetail, type BandRow } from "../../lib/rosterPage";
import { fShort, yearSpan } from "../../lib/format";
import { useApp } from "../appState";
import { Sheet } from "../Sheet";
import { Timeline } from "../Timeline";

export function BandSheet({ id }: { id: number }) {
  const { open, close, version, changed, firstYear } = useApp();
  const [b, setB] = useState<BandDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setFormState] = useState({ name: "", mbid: "" });
  // Any write in the app refetches this sheet; an unsaved edit survives it. (2026-09-11 gate)
  const dirty = useRef(false);
  const setForm = (f: { name: string; mbid: string }) => {
    dirty.current = true;
    setFormState(f);
  };
  const [picking, setPicking] = useState<BandRow[] | null>(null);
  const [pick, setPick] = useState("");

  useEffect(() => {
    let live = true;
    fetchBand(id)
      .then((d) => {
        if (!live) return;
        setB(d);
        if (!dirty.current) setFormState({ name: d.name, mbid: d.setlistfm_mbid ?? "" });
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [id, version]);

  const fail = (e: Error) => setError(e.message);
  const startPick = () => fetchBands("", "name").then((rows) => setPicking(rows.filter((r) => r.id !== b?.id && !r.aliases.length)), fail);
  const group = () => b && pick && addAlias(b.id, Number(pick)).then(() => { setPicking(null); setPick(""); changed("Grouped"); }, fail);

  const dates = b?.shows.map((s) => s.date) ?? [];
  return (
    <Sheet title={b?.name ?? ""} label={b?.name ?? "Band"} onClose={close}>
      {error && <p className="error">{error}</p>}
      {!b && !error && <p className="loading">Loading…</p>}
      {b && (
        <>
          <div className="mono muted" style={{ marginTop: -10 }}>
            Seen {b.times_seen}× · {yearSpan(dates[dates.length - 1] ?? null, dates[0] ?? null)}
          </div>
          <Timeline years={b.years} first={firstYear} big labels />
          <section>
            <div className="sect-h">
              <p className="eyebrow">Alias group</p>
              <span className="meta">shows under these names count here</span>
            </div>
            <div className="alias">
              {b.aliases.map((a) => (
                <span key={a.id}>
                  {a.name}
                  <button type="button" aria-label={`Ungroup ${a.name}`} onClick={() => removeAlias(b.id, a.id).then(() => changed("Ungrouped"), fail)}>✕</button>
                </span>
              ))}
              {picking ? (
                <>
                  <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Band to group" className="chip" style={{ padding: "5px 8px" }}>
                    <option value="">Pick a band…</option>
                    {picking.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                  <button type="button" className="btn sm" disabled={!pick} onClick={group}>Group</button>
                </>
              ) : (
                <button type="button" className="chip" style={{ padding: "5px 11px" }} onClick={startPick}>+ Add a name</button>
              )}
            </div>
          </section>
          <section>
            <p className="eyebrow" style={{ marginBottom: 4 }}>Shows</p>
            <div className="hist">
              {b.shows.map((s) => (
                <button type="button" key={`${s.id}${s.played_as ?? ""}`} onClick={() => open("show", s.id)}>
                  <span className="hd">{fShort(s.date)}</span>
                  <span>
                    {s.venue}
                    {s.played_as && <span className="chip" style={{ marginLeft: 6 }}>as {s.played_as}</span>}
                    <br />
                    <span className="faint" style={{ fontSize: 12 }}>{s.bill}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
          <div className="form">
            <label className="f"><span>Name</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="f"><span>setlist.fm artist ID</span><input value={form.mbid} placeholder="MusicBrainz ID, links setlists" onChange={(e) => setForm({ ...form, mbid: e.target.value })} /></label>
            <button type="button" className="btn" style={{ justifySelf: "start" }} disabled={!form.name.trim()} onClick={() => saveBand(b.id, form.name, form.mbid).then(() => { dirty.current = false; changed("Band saved"); }, fail)}>Save</button>
          </div>
        </>
      )}
    </Sheet>
  );
}
